import React, { useState, useEffect } from 'react';
import './FinalScore.css';

const HAS_CONTEXT_RE = /\b(when|while|during|in my|at my|we had|there was|the (project|team|situation)|previously|last (year|job|role)|at the time)\b/i;
const HAS_ACTION_RE  = /\bi (led|built|created|implemented|developed|decided|took|helped|managed|designed|wrote|fixed|solved|set up|introduced|started|organized|reached|worked on)\b/i;
const HAS_RESULT_RE  = /\b(result|outcome|as a result|led to|ended up|successfully|improved|reduced|increased|learned|ultimately|achieved|delivered|saved|grew|gained|went from)\b/i;

function barInsight(metric, value, wpm) {
  switch (metric) {
    case 'eye':
      if (value >= 85) return 'Excellent — well above the 70% target';
      if (value >= 70) return 'Good — meets the 70% minimum';
      if (value >= 50) return 'Moderate — look at the camera lens, not your face on screen';
      return 'Low — interviewers read this as disengagement or uncertainty';
    case 'pace':
      if (wpm === 0) return 'No speech detected this session';
      if (value >= 90) return `${wpm} WPM — ideal pace, easy to follow`;
      if (value >= 68) return wpm > 160 ? `${wpm} WPM — slightly fast, try pausing at punctuation` : `${wpm} WPM — slightly slow, use shorter punchier sentences`;
      if (value >= 45) return wpm > 160 ? `${wpm} WPM — too fast, risks losing the interviewer` : `${wpm} WPM — too slow, attention drifts between sentences`;
      return wpm > 200 ? `${wpm} WPM — very fast, focus on deliberate pausing` : `${wpm} WPM — very slow, compress answers into fewer words`;
    case 'composure':
      if (value >= 85) return 'Calm and controlled — projects confidence to the interviewer';
      if (value >= 70) return 'Slight tension visible — slow breathing before answering helps';
      if (value >= 50) return 'Moderate nerves — open posture and deliberate pausing help';
      return 'High anxiety signal — practice sessions like this reduce it significantly';
    case 'clarity': {
      const fillerPct = Math.round((100 - value) / 4);
      if (value >= 92) return `${fillerPct}% filler words — very clean delivery`;
      if (value >= 75) return `${fillerPct}% filler — replace 'um/uh' with deliberate silence`;
      if (value >= 50) return `${fillerPct}% filler — high density, use STAR structure to pre-organize`;
      return `${fillerPct}% filler — very high, strongly impacts perceived confidence`;
    }
    default: return null;
  }
}

function AnimatedBar({ label, value, unit = '', ideal = null, insight = null }) {
  const [width, setWidth] = useState(0);
  const color = value >= 75 ? '#4ade80' : value >= 50 ? '#fbbf24' : '#ef4444';
  useEffect(() => {
    const t = setTimeout(() => setWidth(value), 120);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <div className="score-bar-row">
      <div className="score-bar-header">
        <span className="score-bar-label">{label}</span>
        <div className="score-bar-right">
          {ideal && <span className="score-bar-ideal">{ideal}</span>}
          <span className="score-bar-num" style={{ color }}>{value}{unit}</span>
        </div>
      </div>
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      {insight && <p className="score-bar-insight">{insight}</p>}
    </div>
  );
}

const COACHING = {
  eye_contact: {
    icon: '👁',
    title: 'Eye Contact',
    detail: (s) => {
      const pct = s.eyeContact;
      if (pct >= 75) return `Your eye contact averaged ${pct}% — well above the 70% threshold. Interviewers read consistent gaze as attentiveness and confidence. Keep it up.`;
      if (pct >= 50) return `Your eye contact averaged ${pct}%, which is moderate. Aim for 70%+ by looking directly at the camera lens rather than your own face on screen. A useful trick: place a sticky note just above the lens as a visual anchor. Return your gaze to it before delivering each key point.`;
      return `Eye contact was low at ${pct}%. This is one of the strongest non-verbal signals in an interview — low eye contact is often read as uncertainty or disengagement. Practice speaking to a fixed point on your monitor near the camera. Maintain contact for 3–5 second intervals, then briefly break and return.`;
    },
  },
  speech_pace: {
    icon: '🎙',
    title: 'Speech Pace',
    detail: (s) => {
      const wpm = s.wpm;
      if (wpm >= 100 && wpm <= 160) return `Your pace averaged ${wpm} WPM — squarely in the ideal 100–160 WPM range. This makes you easy to follow and projects calm confidence.`;
      if (wpm > 160) return `You averaged ${wpm} WPM, which is ${wpm - 160} WPM above the 160 WPM ceiling. Focus on pausing at punctuation — a one-second breath after each sentence naturally brings pace down without feeling robotic. Slowing down also gives you more time to think before answering.`;
      if (wpm > 0 && wpm < 100) return `You averaged ${wpm} WPM, below the 100 WPM floor. Very slow delivery can lose an interviewer's attention between sentences. Try using shorter, punchier sentences and building answers in three beats: context, main point, conclusion.`;
      return `Speech rate data wasn't captured this session — make sure your microphone is active next time.`;
    },
  },
  composure: {
    icon: '🧘',
    title: 'Composure',
    detail: (s) => {
      const comp = 100 - s.nervousness;
      if (comp >= 75) return `Composure scored ${comp}% — your facial expressions and body language projected calm throughout. This puts interviewers at ease and makes your answers feel more credible.`;
      if (comp >= 50) return `Composure was ${comp}%, showing moderate tension. Before your next session: spend 60 seconds doing slow belly breathing (4 in, 2 hold, 6 out). During the interview, pause before answering even when you know the answer. That deliberate pause reads as confidence.`;
      return `Composure scored ${comp}%, indicating significant tension. Two concrete fixes: (1) Before the call, do 2 minutes of diaphragmatic breathing. (2) During the call, sit up straight with both feet flat — open, grounded posture feeds back to your nervous system and reduces anxiety.`;
    },
  },
  clarity: {
    icon: '💬',
    title: 'Speech Clarity',
    detail: (s) => {
      const filler = s.fillerWords;
      if (filler <= 8) return `Filler word usage was ${filler}% — very clean. Low filler density makes your answers sound prepared and deliberate, which is exactly what interviewers reward.`;
      if (filler <= 15) return `Filler words made up ${filler}% of your speech. The most common culprits — 'um', 'uh', 'like', 'basically' — are thinking-time bridges. The fix: replace them with silence. A one-second pause while you collect your next thought sounds far more confident than 'um'.`;
      return `Filler words were at ${filler}% of your speech — noticeably high. Two strategies: (1) Use the STAR framework to pre-structure answers — this reduces in-the-moment searching. (2) When you feel an 'um' coming, stop completely, breathe, then continue.`;
    },
  },
};

function QBreakdown({ qb, idx }) {
  const composure  = 100 - (qb.stats.nervousness || 0);
  const scoreColor = qb.answerScore >= 75 ? '#4ade80' : qb.answerScore >= 50 ? '#fbbf24' : '#ef4444';
  const label      = qb.answerScore >= 80 ? 'Strong' : qb.answerScore >= 60 ? 'Good' : qb.answerScore >= 40 ? 'Developing' : 'Needs Work';
  const eyeColor   = qb.stats.eyeContact >= 70 ? '#4ade80' : qb.stats.eyeContact >= 50 ? '#fbbf24' : '#ef4444';
  const compColor  = composure >= 75 ? '#4ade80' : composure >= 50 ? '#fbbf24' : '#ef4444';
  const q = qb.question.length > 60 ? qb.question.slice(0, 60) + '…' : qb.question;

  const answerText = qb.answerText || '';
  const hasContext = HAS_CONTEXT_RE.test(answerText);
  const hasAction  = HAS_ACTION_RE.test(answerText);
  const hasResult  = HAS_RESULT_RE.test(answerText);

  return (
    <div className="q-breakdown-item">
      <div className="q-breakdown-header">
        <span className="q-num-badge">Q{idx + 1}</span>
        <span className="q-question-text">"{q}"</span>
        <div className="q-score-box">
          <span className="q-score-num" style={{ color: scoreColor }}>{qb.answerScore}</span>
          <span className="q-score-label" style={{ color: scoreColor }}>{label}</span>
        </div>
      </div>
      <div className="q-meta-row">
        <span style={{ color: eyeColor }}>👁 {qb.stats.eyeContact}%</span>
        <span style={{ color: compColor }}>🧘 {composure}%</span>
        {qb.stats.fillerWords > 0 && <span style={{ color: '#888' }}>💬 {qb.stats.fillerWords}% filler</span>}
        <span style={{ color: '#555' }}>{qb.wordCount} words</span>
      </div>
      <div className="q-star-row">
        <span className="star-label">STAR</span>
        <span className={`star-chip ${hasContext ? 'star-hit' : 'star-miss'}`}>Context</span>
        <span className={`star-chip ${hasAction  ? 'star-hit' : 'star-miss'}`}>Action</span>
        <span className={`star-chip ${hasResult  ? 'star-hit' : 'star-miss'}`}>Result</span>
      </div>
    </div>
  );
}

function FinalScore({ stats, elapsedTime, onRestart, questionBreakdowns = [] }) {
  const eyeScore  = stats.eyeContact ?? 75;
  const wpm       = stats.wpm ?? 0;
  const speechScore = wpm === 0          ? 60
    : wpm >= 100 && wpm <= 160           ? 92
    : wpm >= 80  && wpm <= 200           ? 72
    : wpm >= 60  && wpm <  80            ? 48
    : wpm >  200 && wpm <= 240           ? 48
    : wpm >  240                         ? 28
    : Math.max(10, Math.round(wpm * 0.45));
  const composure = Math.max(0, 100 - (stats.nervousness ?? 0));
  const clarity   = Math.max(0, 100 - (stats.fillerWords ?? 0) * 4);
  const avg       = (eyeScore + speechScore + composure + clarity) / 4;
  const minScore  = Math.min(eyeScore, speechScore, composure, clarity);
  const overall   = Math.round(0.70 * avg + 0.30 * minScore);

  useEffect(() => {
    const grade = overall >= 90 ? 'A' : overall >= 80 ? 'B' : overall >= 70 ? 'C' : overall >= 60 ? 'D' : 'F';
    const entry = {
      date: new Date().toISOString(),
      duration: elapsedTime || 0,
      overall, grade,
      eyeContact: eyeScore, wpm, composure, clarity,
      emotion: stats.emotion || 'neutral',
    };
    try {
      const prev = JSON.parse(localStorage.getItem('ic-session-history') || '[]');
      localStorage.setItem('ic-session-history', JSON.stringify([...prev, entry].slice(-20)));
    } catch {}
  }, []); // runs once on mount — values are stable at this point

  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    const step = overall / 40;
    let cur = 0;
    const t = setInterval(() => {
      cur = Math.min(overall, cur + step);
      setDisplayScore(Math.round(cur));
      if (cur >= overall) clearInterval(t);
    }, 25);
    return () => clearInterval(t);
  }, [overall]);

  const grade      = overall >= 90 ? 'A' : overall >= 80 ? 'B' : overall >= 70 ? 'C' : overall >= 60 ? 'D' : 'F';
  const scoreColor = overall >= 75 ? '#4ade80' : overall >= 55 ? '#fbbf24' : '#ef4444';
  const overallLabel = overall >= 90 ? 'Excellent' : overall >= 80 ? 'Strong' : overall >= 70 ? 'Good' : overall >= 55 ? 'Fair' : 'Needs Work';
  const circ       = 2 * Math.PI * 50;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const scores     = { eye_contact: eyeScore, speech_pace: speechScore, composure, clarity };
  const strengths  = Object.entries(scores).filter(([, v]) => v >= 72).map(([k]) => k);
  const focusAreas = Object.entries(scores).filter(([, v]) => v < 72).map(([k]) => k);

  const wpmColor  = wpm >= 100 && wpm <= 160 ? '#4ade80' : wpm > 0 ? '#fbbf24' : '#666';
  const eyeColor  = eyeScore >= 70 ? '#4ade80' : eyeScore >= 50 ? '#fbbf24' : '#ef4444';
  const compColor = composure >= 75 ? '#4ade80' : composure >= 50 ? '#fbbf24' : '#ef4444';
  const fillColor = clarity >= 75 ? '#4ade80' : clarity >= 50 ? '#fbbf24' : '#ef4444';

  return (
    <div className="final-score">
      <div className="fs-header">
        <h1>session complete</h1>
        <p className="session-meta">{formatTime(elapsedTime || 0)} · {stats.emotion || 'neutral'} dominant emotion</p>
      </div>

      <div className="fs-hero">
        <div className="fs-ring-wrap">
          <div className="score-ring">
            <svg viewBox="0 0 120 120" width="140" height="140">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#1e1e1e" strokeWidth="8" />
              <circle
                cx="60" cy="60" r="50"
                fill="none" stroke={scoreColor} strokeWidth="8"
                strokeDasharray={circ}
                strokeDashoffset={circ * (1 - displayScore / 100)}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 0.05s linear' }}
              />
            </svg>
            <div className="score-inner">
              <span className="score-num" style={{ color: scoreColor }}>{displayScore}</span>
              <span className="score-grade" style={{ color: scoreColor }}>{grade}</span>
            </div>
          </div>
          <p className="score-label-text" style={{ color: scoreColor }}>{overallLabel}</p>
        </div>

        <div className="fs-quick-stats">
          <div className="quick-stat">
            <span className="qs-value" style={{ color: wpmColor }}>{wpm > 0 ? wpm : '—'}</span>
            <span className="qs-label">speech rate</span>
            <span className="qs-sub">wpm</span>
          </div>
          <div className="quick-stat">
            <span className="qs-value" style={{ color: eyeColor }}>{eyeScore}%</span>
            <span className="qs-label">eye contact</span>
            <span className="qs-sub">avg</span>
          </div>
          <div className="quick-stat">
            <span className="qs-value" style={{ color: compColor }}>{composure}%</span>
            <span className="qs-label">composure</span>
            <span className="qs-sub">avg</span>
          </div>
          <div className="quick-stat">
            <span className="qs-value" style={{ color: fillColor }}>{clarity}%</span>
            <span className="qs-label">clarity</span>
            <span className="qs-sub">filler-free</span>
          </div>
        </div>
      </div>

      <div className="breakdown-card">
        <h3>score breakdown</h3>
        <AnimatedBar label="Eye Contact"  value={eyeScore}    unit="%" ideal="target 70%+" insight={barInsight('eye',      eyeScore,    wpm)} />
        <AnimatedBar label="Speech Pace"  value={speechScore}          ideal="100–160 wpm"  insight={barInsight('pace',     speechScore, wpm)} />
        <AnimatedBar label="Composure"    value={composure}   unit="%"                      insight={barInsight('composure',composure,  wpm)} />
        <AnimatedBar label="Clarity"      value={clarity}     unit="%" ideal="low filler"   insight={barInsight('clarity',  clarity,     wpm)} />
      </div>

      {questionBreakdowns.length > 0 && (
        <div className="breakdown-card q-breakdown-card">
          <h3>per question</h3>
          {questionBreakdowns.map((qb, i) => (
            <QBreakdown key={i} qb={qb} idx={i} />
          ))}
        </div>
      )}

      {strengths.length > 0 && (
        <div className="strengths-card">
          <h3>what went well</h3>
          {strengths.map(k => (
            <div key={k} className="strength-item">
              <span className="strength-icon">{COACHING[k].icon}</span>
              <div>
                <span className="strength-title">{COACHING[k].title}</span>
                <p className="strength-text">{COACHING[k].detail(stats)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {focusAreas.length > 0 && (
        <div className="focus-card">
          <h3>focus areas</h3>
          {focusAreas.map(k => (
            <div key={k} className="focus-item">
              <div className="focus-header">
                <span className="focus-icon">{COACHING[k].icon}</span>
                <span className="focus-title">{COACHING[k].title}</span>
              </div>
              <p className="focus-text">{COACHING[k].detail(stats)}</p>
            </div>
          ))}
        </div>
      )}

      {focusAreas.length === 0 && (
        <div className="perfect-card">
          <span className="perfect-icon">🌟</span>
          <p>Excellent session across every metric. Consistency is the hardest part — keep practicing at this level.</p>
        </div>
      )}

      <button className="btn-restart" onClick={onRestart}>practice again</button>
    </div>
  );
}

export default FinalScore;
